import * as React from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, Circle, useToggle } from '@factorialco/deprecated-design-system'
import {
  ArchiveLeaveTypeDocument,
  ArchiveLeaveTypeMutation,
  LeaveTypeFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import type { Option } from 'modules/core/components/Popover/Content'
import PopoverContent from 'modules/core/components/Popover/Content'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import ArchiveLeaveTypeModal from '../Modals/ArchiveLeaveTypeModal'
import styles from './index.module.scss'

interface Props {
  leaveType: LeaveTypeFragment
}

const LeaveTypeItem = ({ leaveType }: Props) => {
  const [open, toggle] = useToggle()
  const [archiveModalOpen, setArchiveModalOpen] = useState(false)

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [archiveLeaveTypeMutation] = useMutation(ArchiveLeaveTypeDocument, {
    extractorFn: (mutationData: ArchiveLeaveTypeMutation) =>
      mutationData.timeoffMutations.updateTimeoffLeaveType,
  })

  const unarchive = () =>
    asyncAction(() => archiveLeaveTypeMutation({ variables: { id: leaveType.id, active: true } }))

  const setArchiveLeaveType = () => setArchiveModalOpen(true)

  const openEditModal = () => {
    navigate(
      navigation.settings.timeoffPolicies.editLeaveTypeId.fullPath({
        id: leaveType.id,
      })
    )
  }

  const isActive = leaveType.active

  const title = isActive
    ? i18n.t('settings.leave_types.item.options.archive')
    : i18n.t('settings.leave_types.item.options.unarchive')

  const optionsIcon = isActive ? 'settings' : 'archive-open'

  const getOptions = (): Option[] => {
    const options: Option[] = []
    if (isActive) {
      options.push({
        onClick: openEditModal,
        title: i18n.t('settings.leave_types.item.options.edit'),
      })
    }

    isActive
      ? options.push({
          title: title,
          onClick: setArchiveLeaveType,
        })
      : options.push({
          title: title,
          onClick: unarchive,
          type: 'wrong',
        })

    return options
  }

  const renderAction = () => {
    return (
      <div className={styles.action} data-intercom-target="leave-type-actions">
        <Popover
          attachment="top right"
          targetAttachment="bottom center"
          toggle={toggle}
          open={open}
        >
          <Button onClick={toggle(true)} type="secondary" iconName={optionsIcon} />
          <PopoverContent onClose={toggle(false)} options={getOptions()} />
        </Popover>
      </div>
    )
  }

  return (
    <div className={styles.item}>
      <div className={styles.content}>
        <div className={styles.circle}>
          <Circle color={leaveType.color} />
        </div>
        <div className={styles.contentInner}>
          <div className={styles.title}>{leaveType.translatedName}</div>
        </div>
      </div>
      {renderAction()}
      {archiveModalOpen && (
        <ArchiveLeaveTypeModal
          leaveTypeId={leaveType.id}
          onClose={() => setArchiveModalOpen(false)}
        />
      )}
    </div>
  )
}

export default observer(LeaveTypeItem)
